import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { ATHENS_GRAY } from "@/controls/shared/constants/colors";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import heatReportStyles from "@/pages/heat-report/styles/useHeatReportStyles";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Grid2 as Grid, Paper, ThemeProvider, Typography } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getChemistryHeatReport } from "../../caster/repositories/ProductionRepository";
import { useHeatReportContext } from "../../meltshop/heat-report/HeatReportContext";
import { searchHeatsWithProcessId } from "../../meltshop/heats/repositories/heatsRepository";
import { CreateSampleWizard } from "./components/CreateSampleWizard";
import { ChemistryHeatReportHeaderFields } from "./components/HeaderFields";
import { SampleData } from "./components/SampleData";
import { ElementTable } from "./components/element-table/ElementTable";
import { EditCastedGrade } from "./components/modals/EditCastedGrade";
import { useSampleEditor } from "./hooks/useSampleEditor";
import "./heatReport.css";

type SearchedHeat = {
	Id: number;
	Name: string;
};

const getStringValue = (dataValue: any | undefined): string => {
	if (dataValue === undefined || dataValue === null) return "";
	return String(dataValue);
};

export const LabDataEntryPage = () => {
	const navigate = useNavigate();
	const { state, actions } = useHeatReportContext();
	const { heatName: heatNameParam } = useParams();

	const [isLoading, setIsLoading] = useState(false);
	const [dataSource, setDataSource] = useState<any[]>([]);
	const [coloringEnabled, setColoringEnabled] = useState(true);
	const [showGradeModal, setShowGradeModal] = useState(false);
	const [showCreateSampleModal, setShowCreateSampleModal] = useState(false);

	const heatId = state.selectedHeat?.HeatId ?? null;
	const gradeId =
		state.chemistryHeatReport?.CastedGradeId ||
		state.chemistryHeatReport?.ProgrammedGradeId ||
		null;

	const getChemistryData = useCallback(async (heatIdToFetch: number | null) => {
		setIsLoading(true);
		const resp = await getChemistryHeatReport(heatIdToFetch);
		if (resp.ok && resp.data && resp.data.tables.length > 1) {
			actions.setChemistryHeatReport(resp.data.tables[0].rows[0]);
			setDataSource(resp.data.tables[1].rows || []);
		} else {
			actions.setChemistryHeatReport(null);
			setDataSource([]);
		}
		setIsLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getHeatByName = useCallback(async (heatNameToSearch: string) => {
		const response = await searchHeatsWithProcessId(heatNameToSearch);
		if (response.ok && response.data?.tables[0]?.rows.length > 0) {
			const heats: SearchedHeat[] = response.data.tables[0].rows;
			const heatToSelect =
				heats.find((h) => h.Name === heatNameToSearch) || heats[0];
			if (heatToSelect) {
				actions.setSelectedHeat({
					HeatId: heatToSelect.Id,
					HeatName: heatToSelect.Name,
					IsExternal: false,
					NextHeatId: null,
					PreviousHeatId: null,
					NextHeatName: null,
					PreviousHeatName: null,
					ProcessId: null,
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Navigation core functions (without confirmation)
	const doNavigateNext = useCallback(() => {
		const heat = state.chemistryHeatReport;
		if (heat?.NextHeatId && heat?.NextHeatName) {
			actions.setSelectedHeat({
				HeatId: heat.NextHeatId,
				HeatName: heat.NextHeatName,
				IsExternal: heat.IsExternal || false,
				NextHeatId: null,
				PreviousHeatId: null,
				NextHeatName: null,
				PreviousHeatName: null,
				ProcessId: null,
			});
			navigate(`/lab-data-entry/${heat.NextHeatName}`);
		}
	}, [state.chemistryHeatReport, actions, navigate]);

	const doNavigatePrevious = useCallback(() => {
		const heat = state.chemistryHeatReport;
		if (heat?.PreviousHeatId && heat?.PreviousHeatName) {
			actions.setSelectedHeat({
				HeatId: heat.PreviousHeatId,
				HeatName: heat.PreviousHeatName,
				IsExternal: heat.IsExternal || false,
				NextHeatId: null,
				PreviousHeatId: null,
				NextHeatName: null,
				PreviousHeatName: null,
				ProcessId: null,
			});
			navigate(`/lab-data-entry/${heat.PreviousHeatName}`);
		}
	}, [state.chemistryHeatReport, actions, navigate]);

	// Sample editing hook
	const onDataRefresh = useCallback(async () => {
		await getChemistryData(heatId);
	}, [getChemistryData, heatId]);

	const {
		selectedSample,
		isSaving,
		isLoadingSample,
		errorMessage,
		notificationMessage,
		showDeleteConfirm,
		showUnsavedConfirm,
		currentSampleIsTooOld,
		MAX_AGE_DAYS,
		isSampleTooOld,
		control,
		handleSubmit,
		setErrorMessage,
		setNotificationMessage,
		handleCellValueChanged,
		handleCellEditingStarted,
		handleCellEditingStopped,
		handleSelectionChanged,
		handleCellClicked,
		handleSampleAttributesSubmit,
		handleSampleSelection,
		handleFieldChange,
		handleCancel,
		handleAddSample,
		handleDeleteSample,
		handleDeleteSampleFromRow,
		confirmIfDirty,
		handleUnsavedConfirmClose,
	} = useSampleEditor({ dataSource, heatId, gradeId, onDataRefresh });

	// Navigation handlers with unsaved changes confirmation
	const handleNext = useCallback(() => {
		confirmIfDirty(doNavigateNext);
	}, [confirmIfDirty, doNavigateNext]);

	const handlePrevious = useCallback(() => {
		confirmIfDirty(doNavigatePrevious);
	}, [confirmIfDirty, doNavigatePrevious]);

	const onAddSample = useCallback(() => {
		if (handleAddSample()) setShowCreateSampleModal(true);
	}, [handleAddSample]);

	// Effects
	useEffect(() => {
		getChemistryData(heatId);
	}, [heatId, getChemistryData]);

	useEffect(() => {
		if (heatNameParam && heatNameParam !== state.selectedHeat?.HeatName) {
			getHeatByName(heatNameParam);
		} else if (!heatNameParam && !state.selectedHeat?.HeatId) {
			getChemistryData(null);
		}
	}, [
		heatNameParam,
		state.selectedHeat?.HeatName,
		state.selectedHeat?.HeatId,
		getHeatByName,
		getChemistryData,
	]);

	const hasReport = state.chemistryHeatReport != null;

	return (
		<>
			<HelmetDexteel
				title={`${state.chemistryHeatReport?.HeatName ? state.chemistryHeatReport.HeatName + " - " : ""} LAB Data Entry`}
			/>
			<Backdrop open={state.isLoadingBackground} style={{ zIndex: 1000 }}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Paper
				elevation={0}
				style={{
					width: "100%",
					padding: "15px 20px 10px",
					minHeight: "calc(100vh - 100px)",
					height: "100%",
					backgroundColor: ATHENS_GRAY,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Grid
					container
					justifyContent="center"
					alignItems="flex-start"
					style={{ flex: 1, overflow: "auto" }}
				>
					<Grid size={{ xs: 12, md: 12 }} style={{ margin: 0 }}>
						<Typography variant="h5" style={{ fontWeight: 600 }}>
							LAB Data Entry
						</Typography>

						{hasReport && (
							<ThemeProvider theme={heatReportStyles}>
								<ChemistryHeatReportHeaderFields
									getStringValue={getStringValue}
									setShowGradeModal={setShowGradeModal}
									getDataFromAPI={getChemistryData}
									onNext={handleNext}
									onPrevious={handlePrevious}
									onNavigateAway={confirmIfDirty}
								/>
							</ThemeProvider>
						)}

						{hasReport && (
							<Grid container>
								<Grid size={{ xs: 12 }}>
									<ElementTable
										elementList={dataSource}
										coloringEnabled={coloringEnabled}
										onToggleColoring={setColoringEnabled}
										editable={true}
										loading={isLoading || isSaving}
										onCellValueChanged={handleCellValueChanged}
										onCellEditingStarted={handleCellEditingStarted}
										onCellEditingStopped={handleCellEditingStopped}
										onSelectionChanged={handleSelectionChanged}
										onCellClicked={handleCellClicked}
										onAddSample={onAddSample}
										gradeId={gradeId}
										onDeleteSample={handleDeleteSampleFromRow}
										isSampleTooOld={isSampleTooOld}
									/>
								</Grid>
							</Grid>
						)}

						{selectedSample && (
							<SampleData
								control={control}
								selectedSample={selectedSample}
								isLoading={isLoadingSample || isSaving}
								isSaving={isSaving}
								isReadOnly={currentSampleIsTooOld}
								readOnlyMessage={
									currentSampleIsTooOld
										? `This sample is older than ${MAX_AGE_DAYS} days and cannot be modified.`
										: undefined
								}
								onSubmit={handleSubmit(handleSampleAttributesSubmit)}
								onSampleSelection={handleSampleSelection}
								onFieldChange={handleFieldChange}
								onEditingStarted={handleCellEditingStarted}
								onEditingStopped={handleCellEditingStopped}
								onCancel={handleCancel}
							/>
						)}
					</Grid>
				</Grid>
			</Paper>

			<CreateSampleWizard
				show={showCreateSampleModal}
				onHide={() => setShowCreateSampleModal(false)}
				heatId={heatId}
				gradeId={gradeId}
				existingSamples={dataSource.map((s: any) => ({
					SampleTypeName: s.SampleTypeName || "",
					SampleSequence: s.SampleSequence,
				}))}
				elementData={dataSource
					.filter(
						(s: any, i: number, self: any[]) =>
							i === self.findIndex((t: any) => t.ElementName === s.ElementName),
					)
					.map((s: any) => ({
						ElementName: s.ElementName,
						ElementId: s.ElementId,
						Order: s.Order,
						Decimals: s.Decimals,
					}))}
				onSampleCreated={async () => {
					await getChemistryData(heatId);
					setNotificationMessage("Sample created successfully.");
				}}
			/>

			<EditCastedGrade
				show={showGradeModal}
				onClose={(shouldUpdate) => {
					setShowGradeModal(false);
					if (shouldUpdate) getChemistryData(heatId);
				}}
				gradeId={state.chemistryHeatReport?.CastedGradeId || null}
				heatId={heatId}
			/>

			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal
				error={errorMessage}
				onHide={() => setErrorMessage("")}
				title="Error saving chemistry result"
			/>
			<ConfirmationDialogRaw
				title="Confirmation"
				message={`Are you sure you want to delete ${selectedSample?.SampleTypeName ?? ""}${selectedSample?.SampleSequence ?? ""}?`}
				open={showDeleteConfirm}
				onClose={handleDeleteSample}
			/>
			<ConfirmationDialogRaw
				title="Unsaved Changes"
				message="You have unsaved changes. Are you sure you want to leave without saving?"
				open={showUnsavedConfirm}
				onClose={handleUnsavedConfirmClose}
			/>
		</>
	);
};
